import { useCallback } from "react";
import { normalizeWorkspacePath, sameWorkspacePath } from "../utils/youlaberWorkspacePath";

const WRITE_TOOLS = new Set(["Write", "Edit", "MultiEdit", "NotebookEdit"]);

const toolFilePath = (input) => {
  if (!input) return "";
  return input.file_path || input.notebook_path || "";
};

let _msgIdCounter = 0;
const nextId = () => `cc-${++_msgIdCounter}`;

// 生成子智能体的工具名：本 CC 研究版为 Agent，上游 Claude Code 为 Task。
const isAgentTool = (name) => name === "Agent" || name === "Task";

// 从后往前找第一个满足条件的下标（兼容旧环境，避免依赖 Array.findLastIndex）。
const findLastIndex = (arr, pred) => {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i], i)) return i;
  }
  return -1;
};

/**
 * 处理来自 GoMAown / CCMA 的 cc_ndjson_stream 消息。
 *
 * 消息格式（后端 → GUI）：
 *   { type: "cc_ndjson_stream", task_id, expert_cmd, ndjson: "<CC NDJSON 原始行>", timestamp }
 *
 * 认知协同关键点（本次新增）：
 *   CC 的每条内层消息在顶层带 parent_tool_use_id。
 *   - 为空 → 主智能体（YouLaber 实验员本人）的发言/工具调用，走原有线性渲染。
 *   - 非空 → 某个研讨子智能体（导师/专家）的内部输出，归集到对应的 agent_turn 卡片，
 *            以体现"是谁在发言、属于哪一次研讨"，而不是和主智能体正文混流。
 *
 * agent_turn 消息形态：
 *   { id, type:"agent_turn", toolId, agentName, agentDesc, promptText,
 *     reply, streamingReply, innerTools:[{toolId,toolName,toolInput,result,isError}],
 *     status:"running"|"done", timestamp }
 */
const useWsCCHandler = ({
  setCCMessages,
  setCCSessionState,
  setCCStatus,
  setCCPendingPermission,
  setCCPendingQuestion,
  setCCTaskInfo,
  setCCTokenUsage,
  setCCInitInfo,
  setCCStreamingText,
  ccStreamingTextRef,
  setCCThinkingText,
  setCCFileTree,
  setCCRoster,
}) => {
  return useCallback((data) => {
    if (data.type === "cc_history_loaded") {
      const messages = Array.isArray(data.messages) ? data.messages : [];
      setCCMessages(messages.map((msg, idx) => ({
        id: msg.id || `cc-history-${data.source || "ccma"}-${idx}-${msg.timestamp || Date.now()}`,
        timestamp: msg.timestamp || Date.now(),
        ...msg,
      })));
      return true;
    }

    if (data.type === "cc_history_cleared") {
      ccStreamingTextRef.current = "";
      setCCStreamingText("");
      setCCThinkingText("");
      setCCMessages([]);
      setCCTaskInfo(null);
      setCCTokenUsage({ input: 0, output: 0, cacheRead: 0, turns: 0 });
      setCCSessionState("idle");
      setCCStatus(data.message || "历史已清空");
      return true;
    }

    if (data.type !== "cc_ndjson_stream") return false;

    let cc;
    try {
      cc = JSON.parse(data.ndjson);
    } catch (e) {
      console.warn("[CC] NDJSON 解析失败:", data.ndjson, e);
      return true;
    }

    const ccType = cc.type || "";
    const parentId = cc.parent_tool_use_id || null;

    // ── 研讨角色名册（认知协同）────────────────────────────────────────
    if (ccType === "youlaber_roster") {
      const agents = Array.isArray(cc.agents) ? cc.agents : [];
      setCCRoster(agents.map((a) => ({
        name: a.name || "",
        description: a.description || "",
        role: a.role || "",
      })).filter((a) => a.name));
      return true;
    }

    // ── agent_turn 辅助：按 toolId 局部更新一张研讨卡片 ────────────────
    // patch 可以是对象，或 (turn) => 部分对象。
    const patchTurn = (toolId, patch) => {
      setCCMessages((prev) => {
        const i = findLastIndex(prev, (m) => m.type === "agent_turn" && m.toolId === toolId);
        if (i === -1) return prev;
        const cur = prev[i];
        const delta = typeof patch === "function" ? patch(cur) : patch;
        if (!delta) return prev;
        const next = [...prev];
        next[i] = { ...cur, ...delta };
        return next;
      });
    };

    // 在某研讨卡片内登记/更新一次内部工具调用
    const patchTurnInnerTool = (toolId, toolUseId, updater) => {
      patchTurn(toolId, (turn) => {
        const tools = Array.isArray(turn.innerTools) ? [...turn.innerTools] : [];
        const j = tools.findIndex((t) => t.toolId === toolUseId);
        if (j === -1) {
          tools.push({ toolId: toolUseId, ...updater({}) });
        } else {
          tools[j] = { ...tools[j], ...updater(tools[j]) };
        }
        return { innerTools: tools };
      });
    };

    // ═══════════════════════════════════════════════════════════════════
    // 子智能体（研讨角色）内部输出：parentId 非空 → 归集到对应 agent_turn
    // ═══════════════════════════════════════════════════════════════════
    if (parentId) {
      if (ccType === "stream_event") {
        const evtType = cc.event?.type;
        const delta = cc.event?.delta;
        if (delta?.type === "text_delta" && delta.text) {
          patchTurn(parentId, (t) => ({ streamingReply: (t.streamingReply || "") + delta.text }));
        } else if (delta?.type === "thinking_delta" && delta.thinking) {
          patchTurn(parentId, (t) => ({ thinking: (t.thinking || "") + delta.thinking }));
        }
        if (evtType === "content_block_start" && cc.event?.content_block?.type === "tool_use") {
          const block = cc.event.content_block;
          patchTurnInnerTool(parentId, block.id, () => ({
            toolName: block.name,
            toolInput: {},
          }));
        }
        if (evtType === "message_stop") {
          // 子智能体一段发言结束：把流式文本并入正式回复
          patchTurn(parentId, (t) => {
            const streamed = (t.streamingReply || "").trim();
            if (!streamed) return { streamingReply: "" };
            return {
              reply: t.reply ? `${t.reply}\n${streamed}` : streamed,
              streamingReply: "",
            };
          });
        }
        return true;
      }

      if (ccType === "assistant") {
        const blocks = Array.isArray(cc.message?.content) ? cc.message.content : [];
        blocks.forEach((block) => {
          if (block.type === "tool_use") {
            patchTurnInnerTool(parentId, block.id, () => ({
              toolName: block.name,
              toolInput: block.input,
            }));
          }
        });
        return true;
      }

      if (ccType === "user") {
        const blocks = Array.isArray(cc.message?.content) ? cc.message.content : [];
        blocks.forEach((block) => {
          if (block.type === "tool_result") {
            const text = typeof block.content === "string"
              ? block.content
              : Array.isArray(block.content)
                ? block.content.map((c) => (typeof c === "string" ? c : c.text || JSON.stringify(c))).join("\n")
                : JSON.stringify(block.content);
            patchTurnInnerTool(parentId, block.tool_use_id, () => ({
              result: text,
              isError: block.is_error === true,
            }));
          }
        });
        return true;
      }

      // 其它带 parentId 的类型（tool_progress 等）：不单独渲染，静默忽略
      return true;
    }

    // ═══════════════════════════════════════════════════════════════════
    // 主智能体（YouLaber 实验员）：parentId 为空 → 原有线性渲染
    // ═══════════════════════════════════════════════════════════════════

    // ── assistant 消息（文本 or 工具调用）──────────────────────────────
    if (ccType === "assistant") {
      const contentBlocks = Array.isArray(cc.message?.content) ? cc.message.content : [];

      contentBlocks.forEach((block) => {
        if (block.type === "thinking") {
          if (block.thinking && block.thinking.trim()) {
            // 去重：partial 快照会重发已完成块，按最近一条 thinking 内容幂等
            setCCMessages((prev) => {
              for (let i = prev.length - 1; i >= 0; i--) {
                const m = prev[i];
                if (m.type === "thinking") {
                  if (m.content === block.thinking) return prev;
                  break;
                }
              }
              return [
                ...prev,
                { id: nextId(), type: "thinking", content: block.thinking, timestamp: Date.now() },
              ];
            });
          }
          setCCThinkingText("");
        } else if (block.type === "text") {
          // text 已由 stream_event/text_delta 实时渲染，这里不再追加
        } else if (block.type === "tool_use") {
          if (isAgentTool(block.name)) {
            // 生成子智能体的工具：更新对应 agent_turn 的身份信息（占位卡已在 content_block_start 建好）
            patchTurn(block.id, {
              agentName: block.input?.subagent_type || "",
              agentDesc: block.input?.description || "",
              promptText: block.input?.prompt || "",
            });
            return;
          }
          // 普通工具：更新已插入的占位卡片 input
          setCCMessages((prev) => {
            const idx = findLastIndex(prev, (m) => m.type === "tool_use" && m.toolId === block.id);
            if (idx === -1) {
              return [...prev, {
                id: nextId(), type: "tool_use",
                toolName: block.name, toolInput: block.input, toolId: block.id,
                timestamp: Date.now(),
              }];
            }
            const updated = [...prev];
            updated[idx] = { ...updated[idx], toolInput: block.input };
            return updated;
          });
        }
      });
      return true;
    }

    // ── user 消息（tool_result / text）──────────────────────────────────
    if (ccType === "user") {
      const contentBlocks = Array.isArray(cc.message?.content) ? cc.message.content : [];
      contentBlocks.forEach((block) => {
        if (block.type === "tool_result") {
          // 若该 tool_result 对应的是一次子智能体调用（Agent 工具），标记该研讨结束，
          // 不再单独渲染其原始 tool_result（内容已在 agent_turn 卡片中呈现）。
          let matchedTurn = false;
          setCCMessages((prev) => {
            const i = findLastIndex(prev, (m) => m.type === "agent_turn" && m.toolId === block.tool_use_id);
            if (i === -1) return prev;
            matchedTurn = true;
            const next = [...prev];
            next[i] = { ...next[i], status: "done" };
            return next;
          });
          if (matchedTurn) return;

          const text = typeof block.content === "string"
            ? block.content
            : Array.isArray(block.content)
              ? block.content.map((c) => (typeof c === "string" ? c : c.text || JSON.stringify(c))).join("\n")
              : JSON.stringify(block.content);
          if (text && text.trim()) {
            setCCMessages((prev) => {
              const tool = prev.find((m) => m.type === "tool_use" && m.toolId === block.tool_use_id);
              const filePath = toolFilePath(tool?.toolInput);
              const next = [
                ...prev,
                {
                  id: nextId(),
                  type: "tool_result",
                  toolUseId: block.tool_use_id,
                  content: text,
                  filePath,
                  isError: block.is_error === true,
                  timestamp: Date.now(),
                },
              ];
              if (
                !block.is_error
                && tool
                && WRITE_TOOLS.has(tool.toolName)
                && filePath
                && !next.some((m) => m.type === "workspace_file_link" && sameWorkspacePath(m.path, filePath))
              ) {
                next.push({
                  id: nextId(),
                  type: "workspace_file_link",
                  path: normalizeWorkspacePath(filePath) || filePath.replace(/\\/g, "/"),
                  name: String(filePath).replace(/\\/g, "/").split("/").pop() || filePath,
                  taskId: data.task_id || "",
                  timestamp: Date.now(),
                });
              }
              return next;
            });
          }
        } else if (block.type === "text" && block.text && block.text.trim()) {
          setCCMessages((prev) => [
            ...prev,
            { id: nextId(), type: "system_note", content: block.text, timestamp: Date.now() },
          ]);
        }
      });
      return true;
    }

    // ── stream_event（逐 token 打字机）──────────────────────────────────
    if (ccType === "stream_event") {
      const evtType = cc.event?.type;
      const delta = cc.event?.delta;
      if (delta?.type === "text_delta" && delta.text) {
        ccStreamingTextRef.current += delta.text;
        setCCStreamingText(ccStreamingTextRef.current);
      } else if (delta?.type === "thinking_delta" && delta.thinking) {
        setCCThinkingText((prev) => prev + delta.thinking);
      }
      if (evtType === "content_block_start" && cc.event?.content_block?.type === "tool_use") {
        const block = cc.event.content_block;
        // 先把当前主智能体 streaming text 固化为消息
        const currentText = ccStreamingTextRef.current;
        if (currentText && currentText.trim()) {
          ccStreamingTextRef.current = "";
          setCCStreamingText("");
          setCCMessages((prev) => [
            ...prev,
            { id: nextId(), type: "text", content: currentText, timestamp: Date.now(), isStreaming: false },
          ]);
        }
        if (isAgentTool(block.name)) {
          // 生成子智能体：插入一张研讨卡片占位（身份等 assistant 快照到来后补全）
          setCCMessages((prev) => [
            ...prev,
            {
              id: nextId(),
              type: "agent_turn",
              toolId: block.id,
              agentName: "",
              agentDesc: "",
              promptText: "",
              reply: "",
              streamingReply: "",
              innerTools: [],
              status: "running",
              timestamp: Date.now(),
            },
          ]);
        } else {
          setCCMessages((prev) => [
            ...prev,
            {
              id: nextId(),
              type: "tool_use",
              toolName: block.name,
              toolInput: {},
              toolId: block.id,
              timestamp: Date.now(),
            },
          ]);
        }
      }
      if (evtType === "message_delta") {
        if (cc.event?.usage) {
          const u = cc.event.usage;
          setCCTokenUsage((prev) => ({
            input: prev.input + (u.input_tokens || 0),
            output: prev.output + (u.output_tokens || 0),
            cacheRead: prev.cacheRead + (u.cache_read_input_tokens || 0),
            turns: prev.turns + 1,
          }));
        }
      }
      if (evtType === "message_stop") {
        const currentText = ccStreamingTextRef.current;
        if (currentText && currentText.trim()) {
          ccStreamingTextRef.current = "";
          setCCStreamingText("");
          setCCMessages((prev) => [
            ...prev,
            { id: nextId(), type: "text", content: currentText, timestamp: Date.now(), isStreaming: false },
          ]);
        } else {
          ccStreamingTextRef.current = "";
          setCCStreamingText("");
        }
        setCCThinkingText("");
      }
      return true;
    }

    // ── result（任务完成/失败）──────────────────────────────────────────
    if (ccType === "result") {
      const isError = cc.is_error === true;
      const u = cc.usage || {};
      setCCTaskInfo({
        task_id: data.task_id,
        cost_usd: cc.total_cost_usd ?? cc.cost_usd ?? null,
        duration_ms: cc.duration_ms ?? null,
        num_turns: cc.num_turns ?? null,
        is_error: isError,
        error_msg: isError ? (cc.error || "任务失败") : null,
      });
      if (u.input_tokens != null || u.output_tokens != null) {
        setCCTokenUsage({
          input: u.input_tokens || 0,
          output: u.output_tokens || 0,
          cacheRead: u.cache_read_input_tokens || 0,
          turns: cc.num_turns || 0,
        });
      }
      const remainingText = ccStreamingTextRef.current;
      if (remainingText && remainingText.trim()) {
        ccStreamingTextRef.current = "";
        setCCStreamingText("");
        setCCMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.type === "text" && last.content === remainingText) return prev;
          return [...prev, { id: nextId(), type: "text", content: remainingText, timestamp: Date.now(), isStreaming: false }];
        });
      } else {
        ccStreamingTextRef.current = "";
        setCCStreamingText("");
      }
      setCCThinkingText("");
      setCCSessionState("idle");
      setCCMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          type: "result",
          is_error: isError,
          cost_usd: cc.cost_usd ?? null,
          duration_ms: cc.duration_ms ?? null,
          timestamp: Date.now(),
        },
      ]);
      return true;
    }

    // ── system.session_state_changed ────────────────────────────────────
    if (
      ccType === "system.session_state_changed" ||
      (ccType === "system" && cc.subtype === "session_state_changed")
    ) {
      const newState = cc.state || "idle";
      setCCSessionState(newState);
      if (newState === "running") {
        ccStreamingTextRef.current = "";
        setCCStreamingText("");
        setCCThinkingText("");
      }
      return true;
    }

    // ── system（init）──────────────────────────────────────────────────
    if (ccType === "system" && cc.subtype === "init") {
      setCCInitInfo({
        cwd: cc.cwd || "",
        permissionMode: cc.permissionMode || "default",
      });
      return true;
    }

    // ── system.status ───────────────────────────────────────────────────
    if (ccType === "system.status") {
      setCCStatus(cc.message || "");
      return true;
    }

    // ── system.compact_boundary ─────────────────────────────────────────
    if (ccType === "system.compact_boundary") {
      setCCMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          type: "system_note",
          content: "上下文已压缩，后续对话将基于压缩后的摘要继续。",
          timestamp: Date.now(),
        },
      ]);
      setCCStatus("上下文已压缩");
      return true;
    }

    // ── control_request（权限审批）──────────────────────────────────────
    if (ccType === "control_request" && cc.request?.subtype === "can_use_tool") {
      if (cc.request?.tool_name === "AskUserQuestion") {
        const input = cc.request?.input || {};
        const questions = input.questions || [];
        if (questions.length > 0) {
          setCCPendingQuestion({
            tool_use_id: cc.request_id,
            questions,
          });
          setCCSessionState("requires_action");
        }
      }
      return true;
    }

    // ── system.local_command_output（终端输出）──────────────────────────
    if (ccType === "system.local_command_output") {
      setCCMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          type: "terminal",
          content: cc.output || "",
          command: cc.command || "",
          timestamp: Date.now(),
        },
      ]);
      return true;
    }

    // ── tool_progress（工具执行进度）────────────────────────────────────
    if (ccType === "tool_progress") {
      setCCMessages((prev) => {
        const idx = findLastIndex(prev, (m) => m.type === "tool_use" && m.toolId === cc.tool_use_id);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], elapsed_ms: cc.elapsed_ms };
        return updated;
      });
      return true;
    }

    // ── system.files_persisted（文件变更）──────────────────────────────
    if (ccType === "system.files_persisted") {
      const files = cc.files || [];
      setCCFileTree((prev) => {
        const next = { ...prev };
        files.forEach((f) => {
          next[f.path] = { size: f.size, modified: Date.now() };
        });
        return next;
      });
      return true;
    }

    // ── file_changed ────────────────────────────────────────────────────
    if (ccType === "file_changed" && cc.path) {
      setCCFileTree((prev) => ({
        ...prev,
        [cc.path]: {
          ...(prev[cc.path] || {}),
          modified: Date.now(),
          changed: true,
        },
      }));
      return true;
    }

    // ── workspace_file_link ─────────────────────────────────────────────
    if (ccType === "workspace_file_link" && cc.path) {
      const rawLink = String(cc.path).replace(/\\/g, "/");
      if (/chatlog\.json$/i.test(rawLink)) {
        return true;
      }
      const linkPath = normalizeWorkspacePath(cc.path) || rawLink;
      setCCMessages((prev) => {
        if (prev.some((m) => m.type === "workspace_file_link" && sameWorkspacePath(m.path, linkPath))) {
          return prev;
        }
        return [
          ...prev,
          {
            id: nextId(),
            type: "workspace_file_link",
            path: linkPath,
            name: cc.name || String(cc.path).replace(/\\/g, "/").split("/").pop() || cc.path,
            taskId: cc.task_id || data.task_id || "",
            timestamp: Date.now(),
          },
        ];
      });
      return true;
    }

    // ── rate_limit_event / system.api_retry ─────────────────────────────
    if (ccType === "rate_limit_event" || ccType === "system.api_retry") {
      setCCStatus("API 限流，正在重试...");
      return true;
    }

    // ── tool_use_summary ────────────────────────────────────────────────
    if (ccType === "tool_use_summary") {
      setCCMessages((prev) => [
        ...prev,
        { id: nextId(), type: "summary", content: cc.summary || "", timestamp: Date.now() },
      ]);
      return true;
    }

    // ── prompt_suggestion ───────────────────────────────────────────────
    if (ccType === "prompt_suggestion") {
      setCCMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          type: "suggestion",
          suggestions: cc.suggestions || [],
          timestamp: Date.now(),
        },
      ]);
      return true;
    }

    // 其他未识别类型：静默忽略
    return true;
  }, [
    ccStreamingTextRef,
    setCCFileTree,
    setCCInitInfo,
    setCCMessages,
    setCCPendingPermission,
    setCCPendingQuestion,
    setCCRoster,
    setCCSessionState,
    setCCStatus,
    setCCStreamingText,
    setCCTaskInfo,
    setCCThinkingText,
    setCCTokenUsage,
  ]);
};

export default useWsCCHandler;
